import {SecUtil} from '@/common/utils/security/SecUtil';
import {BaseApi} from '@/common/api/BaseApi';
import {TrialApi} from '@/common/api/trial/TrialApi';
import {ref} from 'vue';
import {router} from '@/common/router';
import {RouteLocationNormalizedLoaded} from 'vue-router';
import {SecUtilHasRoles} from '@/types/common/utils/SecUtilInterfaces';
import {CustomerUserData, UserData} from '@/types/composables/layout/IBaseLayout';
import {Routes} from '@/common/enums/Routes';
import {useAuth} from '@/composables/useAuth';
import {useApiClient} from '@/common/api/useApiClient';
import {useStore} from '@/common/store/store';
import {Permissions} from '@simpleforyou/coaching-shared';

export const initialized = ref(false);
export const layoutLoading = ref(false);

export async function checkRole(to: RouteLocationNormalizedLoaded) {
  if (to.meta.hasOwnProperty('roles')) {
    let access = false;
    if (SecUtil.hasMultipleRoles({or: to.meta.roles} as SecUtilHasRoles)) {
      access = true;
    }
    if (!access) {
      await redirectToModule();
    }
  }
}

export async function checkCoaching(to: RouteLocationNormalizedLoaded) {
  if (to.meta.hasOwnProperty('coaching')) {
    let access = false;
    const metaProperty = to.meta.coaching as string;
    if (SecUtil.coaching(metaProperty)) {
      access = true;
    }
    if (!access) {
      await redirectToModule();
    }
  }
}

export async function checkUserType(to: RouteLocationNormalizedLoaded) {
  if (to.meta.hasOwnProperty('userType')) {
    const store = useStore();
    if (to.meta.userType === 'admin' && !store.isAdmin) {
      await redirectToModule();
    }
  }
}

export async function redirectToModule() {
  const store = useStore();
  if (store.isAdmin) {
    if (SecUtil.hasRoles(Permissions.ROLE_MODULE_INDEX)) {
      await router.push({name: Routes.MODULE_LIST});
    } else {
      await router.push({name: Routes.USER_PROFILE});
    }
  } else {
    await router.push({name: Routes.USER_DASHBOARD});
  }
}

export async function initialize() {
  const {logout} = useAuth();
  const store = useStore();
  const apiClient = useApiClient();
  // Who is logged in is never read from the cache: a stale entry from the
  // previous account in this tab would hand its role to the new one
  // (T00N1NIEPQBA).
  apiClient.removeCacheKey(BaseApi.userData());
  await apiClient.get<UserData | CustomerUserData>(BaseApi.userData(), false).then(async (response) => {
    if (response.status === 200) {
      store.setUserData(response.data);
      await loadTrialStatus();
      initialized.value = true;
      await checkRole(router.currentRoute.value);
      await checkCoaching(router.currentRoute.value);
      await checkUserType(router.currentRoute.value);
      if (store.userData!.password_change_required) {
        await router.push({name: Routes.PASSWORD});
      }
    } else {
      await logout();
    }
  }).catch(async () => {
    await logout();
  });
}

// Pull the trial status so the app can pre-hide trial-blocked controls.
// Failures are non-fatal: a missing/erroring status simply means "no trial".
export async function loadTrialStatus() {
  const store = useStore();
  const apiClient = useApiClient();
  await apiClient.get<{active: boolean; locked: boolean; decision: 'cancelled' | 'continue' | null}>(TrialApi.status(), false).then((response) => {
    const data = response.status === 200 ? response.data : undefined;
    store.setTrialActive(data?.active === true);
    store.setTrialLock(data?.locked === true, data?.decision ?? null);
  }).catch(() => {
    store.setTrialActive(false);
    store.setTrialLock(false, null);
  });
}
